import React from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";

import ContactSupportLink from "components/ContactSupportLink";
import { useFeature } from "components/OrganizationFeatures";
import ShowFlash from "components/ShowFlash";
import { useAppConfig } from "components/WithAppConfig";
import { useRoutes } from "utils/routes";

const WebsocketUnhealthy: React.FunctionComponent<{
  serverId: string;
  hasRecentSnapshot: boolean;
  createdAt: number;
  connectionAge: number;
  usesLegacyHttpApi: boolean;
}> = ({
  serverId,
  hasRecentSnapshot,
  createdAt,
  connectionAge,
  usesLegacyHttpApi,
}) => {
  const { enterpriseEdition } = useAppConfig();
  const { collectorSettings } = useRoutes();
  if (useFeature("hideWebsocketWarning")) {
    return null; // Enterprise flag to disable the warning
  }
  if (!hasRecentSnapshot) {
    return null;
  }
  if (moment.unix(createdAt).isAfter(moment().subtract(1, "day"))) {
    return null; // Don't show if server was just created
  }
  if (connectionAge && connectionAge > 60 * 60) {
    return null;
  }
  if (!connectionAge && !usesLegacyHttpApi) {
    return null; // Servers that are not yet no "recent" snapshot, may not have any websockets data if they recently became inactive
  }
  let message;
  if (connectionAge) {
    message = (
      <>
        Your collector's websocket connection is unstable, lasting for{" "}
        {connectionAge} seconds on average, which can cause missing statistics.
        Please review your collectors connection history on the{" "}
        <Link to={collectorSettings(serverId)}>Collector Settings page</Link>{" "}
        and <ContactSupportLink>reach out to support</ContactSupportLink> if
        needed.
      </>
    );
  } else {
    const deadline = enterpriseEdition ? "an upcoming release" : "2026";
    message =
      "Your collector is using the legacy HTTP snapshot API which will be removed in " +
      deadline +
      ". Please upgrade the collector and ensure your network configuration supports persistent websocket connections.";
  }
  return (
    <ShowFlash
      flashId={`collector-websocket-unhealthy-${serverId}`}
      dismissable
      persist
      level={"alert"}
      msg={message}
    />
  );
};

export default WebsocketUnhealthy;
